import { useState, useEffect } from 'react';
import { eodhd } from '../services/eodhd';
import type { NewsArticle } from '../types';
import { ExternalLink, Calendar, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface AssetNewsProps {
  symbol: string;
  limit?: number;
}

export default function AssetNews({ symbol, limit = 10 }: AssetNewsProps) {
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadNews();
  }, [symbol]);

  const loadNews = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await eodhd.getNews([symbol], limit);
      setNews(response.articles);
    } catch (err) {
      console.error('Failed to load news:', err);
      setError('Failed to load news articles');
    } finally {
      setLoading(false);
    }
  };

  const getSentimentIcon = (sentiment?: string) => {
    if (!sentiment) return null;

    const lower = sentiment.toLowerCase();
    if (lower.includes('positive') || lower.includes('bullish')) {
      return <TrendingUp className="w-4 h-4 text-green-400" />;
    } else if (lower.includes('negative') || lower.includes('bearish')) {
      return <TrendingDown className="w-4 h-4 text-red-400" />;
    }
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getSentimentColor = (sentiment?: string) => {
    if (!sentiment) return 'text-gray-400';

    const lower = sentiment.toLowerCase();
    if (lower.includes('positive') || lower.includes('bullish')) {
      return 'text-green-400';
    } else if (lower.includes('negative') || lower.includes('bearish')) {
      return 'text-red-400';
    }
    return 'text-gray-400';
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);

      if (diffHours < 1) {
        const diffMins = Math.floor(diffMs / (1000 * 60));
        return `${diffMins}m ago`;
      } else if (diffHours < 24) {
        return `${diffHours}h ago`;
      } else if (diffDays < 7) {
        return `${diffDays}d ago`;
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <div className="h-5 w-32 bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 bg-gray-700 rounded animate-pulse w-3/4" />
              <div className="h-3 bg-gray-700 rounded animate-pulse w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <p className="text-red-400 text-sm">{error}</p>
        <button
          onClick={loadNews}
          className="mt-2 text-purple-400 hover:text-purple-300 text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  if (news.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Latest News</h3>
        <p className="text-gray-400 text-sm">No recent news articles found for this asset.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-lg font-semibold mb-4">Latest News</h3>
      <div className="space-y-4">
        {news.map((article, index) => (
          <a
            key={index}
            href={article.link}
            target="_blank"
            rel="noopener noreferrer"
            className="block group hover:bg-gray-750 rounded-lg p-4 transition-colors border border-gray-700 hover:border-gray-600"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-100 group-hover:text-purple-400 transition-colors line-clamp-2 mb-2">
                  {article.title}
                </h4>

                {article.content && (
                  <p className="text-sm text-gray-400 line-clamp-2 mb-2">
                    {article.content}
                  </p>
                )}

                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(article.date)}</span>
                  </div>

                  {article.sentiment && (
                    <div className={`flex items-center gap-1 ${getSentimentColor(article.sentiment)}`}>
                      {getSentimentIcon(article.sentiment)}
                      <span className="capitalize">{article.sentiment}</span>
                    </div>
                  )}

                  {article.tags && article.tags.length > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="text-gray-600">•</span>
                      <span className="truncate max-w-[200px]">
                        {article.tags.slice(0, 2).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-purple-400 transition-colors flex-shrink-0" />
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
